import { useAuth0 } from '@auth0/auth0-vue'
import { defineStore, storeToRefs } from 'pinia'
import { ref, watch } from 'vue'

import { computed } from 'vue'
import { useFeatureFlagAuth } from '@/libs/featureFlagAuth/featureFlagAuth'
import { useFlagStore } from '@/libs/flags'

/**
 * Store to keep track of the loading state of the app.
 * intended to show/hide LoadingScreen component.
 */
export const useAppLoadStore = defineStore('appLoadState', () => {
  const auth0 = useAuth0()
  const { isLoading: flagAuthLoading } = useFeatureFlagAuth()
  const flagStore = useFlagStore()
  const { isLoaded: flagsAreLoaded } = storeToRefs(flagStore)

  // We manually set the loading state to show the loading page
  // during authentication redirects.
  const isLoading = ref(true)
  const startLoading = () => {
    isLoading.value = true
  }
  const endLoading = () => {
    isLoading.value = false
  }

  /**
   * Centralized loading state logic
   * App shows loading screen until ALL of these are ready:
   * 1. Auth0 has finished loading
   * 2. /whoami request has completed (if authenticated)
   * 3. Feature flag client has been initialized
   */
  const isAppReady = computed(() => {
    if (auth0.isLoading.value) {
      return false
    }

    if (auth0.isAuthenticated.value && flagAuthLoading.value) {
      return false
    }

    if (!flagsAreLoaded.value) {
      return false
    }

    return true
  })

  // Automatically clear manual loading when app is ready
  watch(
    isAppReady,
    (ready) => {
      if (ready) {
        endLoading()
      }
    },
    { immediate: true }
  )

  const isAppLoading = computed(() => {
    return isLoading.value || !isAppReady.value
  })

  return { isLoading: isAppLoading, startLoading, endLoading }
})
